import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link } from 'react-router-dom';

import {
  FaLaptopCode,
  FaChartLine,
  FaUserShield,
  FaMoneyBillWave,
  FaUserTie,
  FaBullhorn,
  FaHandshake,
  FaArrowRight,
  FaHeadset,
  FaCheckCircle,
  FaCalculator,
  FaUsers,
  FaGlobe,
  FaRegChartBar,
  FaHourglassHalf,
  FaUserCog,
  FaGift,
  FaComments,
  FaShieldAlt,
  FaFingerprint,
  FaBriefcase,
  FaFileAlt,
  FaLock,
  FaMobileAlt,
  FaGavel,
  FaCreditCard,
  FaBuilding,
  FaDatabase,
  FaBalanceScale,
  FaClipboardList,
  FaUserFriends,
  FaMoneyBillAlt,
  FaFileSignature,
  FaIdCard,
  FaCogs,
  FaDesktop,
  FaShoppingCart,
  FaCode,
  FaBrush,
  FaFacebookF,
  FaInstagram,
  FaSearchDollar,
  FaShareAlt,
  FaChartPie,
  FaLightbulb,
  FaRocket,
  FaServer,
  FaCubes,
  FaStar,
  FaCoins
} from 'react-icons/fa';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({
      duration: 1200,
      once: true,
      offset: 150,
      easing: 'ease-out-cubic',
    });
  }, []);

  const serviceCategories = [
    {
      icon: <FaHeadset size={50} className="text-info mb-3" />,
      detailIcon: <FaComments size={50} className="text-info mb-3" />,
      title: 'Customer Retention & CPE',
      description: 'Maximize customer lifetime value by enhancing satisfaction and loyalty. Our strategies focus on personalized engagement, feedback loops, and proactive support to build lasting relationships.',
      link: '/retention-cpe',
      details: [
        { feature: 'Personalized Follow-ups', icon: <FaHourglassHalf className="me-2 text-gold" /> },
        { feature: 'Customer Experience Enhancement', icon: <FaUserCog className="me-2 text-gold" /> },
        { feature: 'Loyalty Program Integration', icon: <FaGift className="me-2 text-gold" /> },
        { feature: 'Feedback & Survey Management', icon: <FaComments className="me-2 text-gold" /> },
        { feature: 'Churn Prediction & Prevention', icon: <FaShieldAlt className="me-2 text-gold" /> }
      ],
    },
    {
      icon: <FaCheckCircle size={50} className="text-success mb-3" />,
      detailIcon: <FaUserShield size={50} className="text-success mb-3" />,
      title: 'Customer Verification Services',
      description: 'Ensure secure and seamless onboarding with robust verification processes. We mitigate fraud risks, enhance compliance, and streamline identity checks for a trustworthy customer base.',
      link: '/customer-verification',
      details: [
        { feature: 'Real-time KYC & Identity Verification', icon: <FaFingerprint className="me-2 text-gold" /> },
        { feature: 'Background Verification', icon: <FaBriefcase className="me-2 text-gold" /> },
        { feature: 'Address & Document Validation', icon: <FaFileAlt className="me-2 text-gold" /> },
        { feature: 'Fraud Detection & Prevention', icon: <FaLock className="me-2 text-gold" /> },
        { feature: 'Biometric Verification', icon: <FaMobileAlt className="me-2 text-gold" /> }
      ],
    },
    {
      icon: <FaMoneyBillWave size={50} className="text-warning mb-3" />,
      detailIcon: <FaCoins size={50} className="text-warning mb-3" />,
      title: 'Comprehensive Financial Services',
      description: 'Navigate complex financial landscapes with expert advisory, meticulous accounting, and proactive compliance management. From audits to strategic planning, we safeguard your financial health.',
      link: '/financial-services',
      details: [
        { feature: 'Debt Recovery Solutions', icon: <FaGavel className="me-2 text-gold" /> },
        { feature: 'EMI Collection & Management', icon: <FaCreditCard className="me-2 text-gold" /> },
        { feature: 'Financial B2B/B2C Solutions', icon: <FaBuilding className="me-2 text-gold" /> },
        { feature: 'Accounting & Bookkeeping', icon: <FaDatabase className="me-2 text-gold" /> },
        { feature: 'Compliance & Advisory', icon: <FaBalanceScale className="me-2 text-gold" /> }
      ],
    },
    {
      icon: <FaUserTie size={50} className="text-light-blue mb-3" />,
      detailIcon: <FaUsers size={50} className="text-light-blue mb-3" />,
      title: 'End-to-End HR Services',
      description: 'Optimize your human resources with our full suite of services, including talent acquisition, flexible staffing, accurate payroll management, and comprehensive statutory compliance.',
      link: '/hr-services',
      details: [
        { feature: 'Talent Acquisition & Recruitment', icon: <FaClipboardList className="me-2 text-gold" /> },
        { feature: 'Contractual & Permanent Staffing', icon: <FaUserFriends className="me-2 text-gold" /> },
        { feature: 'Payroll Management', icon: <FaMoneyBillAlt className="me-2 text-gold" /> },
        { feature: 'Statutory Compliance', icon: <FaFileSignature className="me-2 text-gold" /> },
        { feature: 'Background Verification (BGV)', icon: <FaIdCard className="me-2 text-gold" /> }
      ],
    },
    {
      icon: <FaGlobe size={50} className="text-gold mb-3" />,
      detailIcon: <FaLaptopCode size={50} className="text-gold mb-3" />,
      title: 'Web & Software Development',
      description: 'Transform your digital presence with custom-built websites, e-commerce solutions, and robust software applications. We use cutting-edge technologies to bring your vision to life with dynamic, scalable solutions.',
      link: '/web-development',
      details: [
        { feature: 'Custom Software Development (ERP, CRM)', icon: <FaCogs className="me-2 text-gold" /> },
        { feature: 'Dynamic Website Design & Development', icon: <FaDesktop className="me-2 text-gold" /> },
        { feature: 'E-commerce Platforms (WordPress, Shopify)', icon: <FaShoppingCart className="me-2 text-gold" /> },
        { feature: 'PHP, React, MERN Stack Development', icon: <FaCode className="me-2 text-gold" /> },
        { feature: 'Mobile-First & Responsive Design', icon: <FaBrush className="me-2 text-gold" /> }
      ],
    },
    {
      icon: <FaRegChartBar size={50} className="text-danger mb-3" />,
      detailIcon: <FaBullhorn size={50} className="text-danger mb-3" />,
      title: 'Results-Driven Digital Marketing',
      description: 'Amplify your brand\'s reach and engagement with data-backed digital marketing strategies. Specializing in Meta Ads and Instagram boosts, we drive targeted traffic and convert leads into loyal customers.',
      link: '/digital-marketing',
      details: [
        { feature: 'Meta Ads (Facebook & Instagram)', icon: <FaFacebookF className="me-2 text-gold" /> },
        { feature: 'Instagram Boosts & Organic Growth', icon: <FaInstagram className="me-2 text-gold" /> },
        { feature: 'SEO & Search Engine Marketing', icon: <FaSearchDollar className="me-2 text-gold" /> },
        { feature: 'Social Media Management', icon: <FaShareAlt className="me-2 text-gold" /> },
        { feature: 'Performance Marketing & Analytics', icon: <FaChartPie className="me-2 text-gold" /> }
      ],
    },
  ];

  return (
    <div style={{ backgroundColor: '#1A232F', color: '#E0E0E0', minHeight: '100vh', fontFamily: "'Poppins', sans-serif" }}>

      {/* Hero Section - Revamped */}
      <section className="py-5 text-center text-md-start hero-section-new">
        <div className="background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="container position-relative z-index-1">
          <div className="row align-items-center">
            <div className="col-md-7" data-aos="fade-right" data-aos-delay="100">
              <h1 className="fw-bold display-3 mb-3 hero-title-gradient">
                <span className="d-block">Our Tailored</span>
                <span className="d-block">Solutions for</span>
                <span className="d-block">Your Success</span>
              </h1>
              <p className="lead text-light-gray mb-4 hero-description-new">
                We empower your business with a comprehensive suite of professional services, meticulously crafted to drive growth, efficiency, and lasting impact.
                <ul className="mt-3 service-list-hero">
                  <li>Human Resources</li>
                  <li>Equipment Recovery</li>
                  <li>Financial Services</li>
                  <li>Customer Verification</li>
                  <li>Web Development & Solutions</li>
                  <li>Digital Marketing</li>
                </ul>
              </p>
              <Link
                to="/contact"
                className="btn btn-lg rounded-pill hero-cta-button-new"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                Start Your Partnership
                <FaArrowRight className="ms-2" />
              </Link>
            </div>
            <div className="col-md-5 text-center mt-5 mt-md-0 d-flex justify-content-center align-items-center" data-aos="zoom-in" data-aos-delay="200">
              <div className="hero-icon-container">
                <FaHandshake size={200} className="text-gold hero-icon-animated" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* All Services Grid Overview */}
      <section className="py-5 bg-gradient-dark">
        <div className="container">
          <h2 className="text-center fw-bold text-gold mb-5 section-title" data-aos="fade-up">
            Our Core Service Offerings
          </h2>
          <div className="row g-4 justify-content-center">
            {serviceCategories.map((service, index) => (
              <div
                key={index}
                className="col-12 col-md-6 col-lg-4"
                data-aos="fade-up"
                data-aos-delay={index * 100 + 100}
              >
                <div className="p-4 rounded-4 shadow bg-dark-card h-100 text-center service-card-hover d-flex flex-column">
                  <div className="flex-grow-1">
                    {service.icon}
                    <h5 className="fw-bold text-white mt-3 mb-2 service-card-title">{service.title}</h5>
                    <p className="text-light-gray fs-6 service-card-description">{service.description}</p>
                  </div>
                  <Link
                    to={service.link}
                    className="btn btn-outline-light mt-4 rounded-pill stretched-link learn-more-button"
                  >
                    Learn More <FaArrowRight className="ms-2" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* Detailed Service Sections */}
      {serviceCategories.map((service, index) => (
        <section
          key={`detail-${index}`}
          className="py-5 detailed-service-section"
          style={{ backgroundColor: index % 2 === 0 ? '#1A232F' : '#202C39' }}
        >
          <div className="container">
            <h2 className="text-center fw-bold text-gold mb-5 section-title" data-aos="fade-up">
              {service.title} - In Detail
            </h2>
            <div className={`row align-items-center ${index % 2 === 0 ? '' : 'flex-row-reverse'} mb-5`}>
              <div
                className="col-md-6 text-center text-md-start"
                data-aos={index % 2 === 0 ? 'fade-right' : 'fade-left'}
                data-aos-delay="100"
              >
                <div className="service-icon-display rounded-circle d-flex align-items-center justify-content-center mx-auto mx-md-0 mb-4 shadow-lg"
                     style={{
                       backgroundColor: index % 2 === 0 ? 'rgba(255, 215, 0, 0.1)' : 'rgba(0, 188, 212, 0.1)',
                     }}
                     data-aos="zoom-in" data-aos-delay="150"
                >
                  {React.cloneElement(service.icon, { size: 100, className: `service-large-icon ${service.icon.props.className.replace('mb-3', '')}` })}
                </div>

                <h3 className="fw-bold text-white mb-3 detailed-service-title">{service.title}</h3>
                <p className="text-light-gray mb-4 detailed-service-description">
                  {service.title === 'Customer Retention & CPE' && 'Dive deeper into how our expertise in Customer Retention & CPE can transform your business. We go beyond basic services to deliver comprehensive solutions tailored to your unique challenges.'}
                  {service.title === 'Customer Verification Services' && 'Our Customer Verification Services are designed to create a secure and trusted environment for your operations. Explore the various layers of verification we implement to protect your business and customers.'}
                  {service.title === 'Comprehensive Financial Services' && 'Gain complete control and clarity over your finances with our Comprehensive Financial Services. We offer a strategic approach to financial management, ensuring compliance and optimizing your fiscal performance.'}
                  {service.title === 'End-to-End HR Services' && 'Unlock the full potential of your workforce with our End-to-End HR Services. From attracting top talent to ensuring seamless daily operations and compliance, we handle every HR aspect with precision.'}
                  {service.title === 'Web & Software Development' && 'Revolutionize your digital footprint with our Web & Software Development services. We build powerful online experiences and intelligent software, turning your ideas into tangible, high-performing assets.'}
                  {service.title === 'Results-Driven Digital Marketing' && 'Make significant noise in your market with our Results-Driven Digital Marketing. We specialize in strategic campaigns that not only boost your visibility but also drive direct conversions and build a loyal customer base.'}
                </p>
                <ul className="list-unstyled text-light-gray detailed-feature-list">
                  {service.details.map((detail, dIdx) => (
                    <li key={dIdx} className="mb-2" data-aos="fade-up" data-aos-delay={dIdx * 100 + 200}>
                      {detail.icon} {detail.feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.link}
                  className="btn btn-outline-light mt-4 rounded-pill discover-more-button"
                  style={{
                    borderColor: service.icon.props.className.includes('text-info') ? '#00BCD4' : service.icon.props.className.includes('text-success') ? '#4CAF50' : service.icon.props.className.includes('text-warning') ? '#FFC107' : service.icon.props.className.includes('text-light-blue') ? '#00BFFF' : service.icon.props.className.includes('text-gold') ? '#FFD700' : '#F44336',
                    color: service.icon.props.className.includes('text-info') ? '#00BCD4' : service.icon.props.className.includes('text-success') ? '#4CAF50' : service.icon.props.className.includes('text-warning') ? '#FFC107' : service.icon.props.className.includes('text-light-blue') ? '#00BFFF' : service.icon.props.className.includes('text-gold') ? '#FFD700' : '#F44336',
                  }}
                  data-aos="fade-up" data-aos-delay={service.details.length * 100 + 300}
                >
                  Discover More about {service.title} <FaArrowRight className="ms-2" />
                </Link>
              </div>
              <div
                className="col-md-6 text-center mt-4 mt-md-0"
                data-aos="zoom-in"
                data-aos-delay="100"
              >
                <div className="service-icon-display-large rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-lg"
                     style={{
                       backgroundColor: index % 2 === 0 ? 'rgba(255, 215, 0, 0.15)' : 'rgba(0, 188, 212, 0.15)',
                       boxShadow: '0 0 25px rgba(255, 215, 0, 0.3)',
                     }}
                     data-aos="zoom-in" data-aos-delay="250"
                >
                  {React.cloneElement(service.detailIcon, { size: 150, className: `service-large-icon ${service.detailIcon.props.className.replace('mb-3', '')}` })}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      <hr className="divider" />

      {/* Section: Why Choose Our Services (General - Consolidated) */}
      <section className="py-5 bg-dark-card">
        <div className="container">
          <h2 className="text-center fw-bold text-gold mb-5 section-title" data-aos="fade-up">
            Why We Are Your Ideal Partner
          </h2>
          <p className="lead text-light-gray mx-auto mb-5 text-center why-choose-description" data-aos="fade-up" data-aos-delay="100">
            Our commitment to your success is unwavering. We combine deep industry knowledge with innovative solutions to deliver unmatched value across all our services.
          </p>
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 shadow-lg border-0 bg-darker-card text-light-gray p-4 rounded-4 service-card-hover">
                <div className="card-body text-center d-flex flex-column justify-content-between">
                  <FaChartLine size={50} className="text-success mb-3 icon-glow" />
                  <h4 className="fw-bold text-white mb-3 why-choose-title">Proven Results</h4>
                  <p className="card-text text-light-gray why-choose-text">
                    We focus on strategies that yield measurable outcomes, ensuring a significant return on your investment and sustainable growth.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100 shadow-lg border-0 bg-darker-card text-light-gray p-4 rounded-4 service-card-hover">
                <div className="card-body text-center d-flex flex-column justify-content-between">
                  <FaLightbulb size={50} className="text-warning mb-3 icon-glow" />
                  <h4 className="fw-bold text-white mb-3 why-choose-title">Innovative Solutions</h4>
                  <p className="card-text text-light-gray why-choose-text">
                    Leveraging cutting-edge technologies and creative thinking to solve complex problems and drive efficiency.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="400">
              <div className="card h-100 shadow-lg border-0 bg-darker-card text-light-gray p-4 rounded-4 service-card-hover">
                <div className="card-body text-center d-flex flex-column justify-content-between">
                  <FaHandshake size={50} className="text-info mb-3 icon-glow" />
                  <h4 className="fw-bold text-white mb-3 why-choose-title">Client-Centric Partnership</h4>
                  <p className="card-text text-light-gray why-choose-text">
                    Your goals are our priority. We offer transparent communication and dedicated support, building trust every step of the way.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* Final Call to Action */}
      <section className="py-5 text-center call-to-action-section" data-aos="fade-up">
        <div className="container">
          <h2 className="fw-bold display-5 text-gold mb-4 call-to-action-title">
            Ready to Accelerate Your Business Growth?
          </h2>
          <p className="lead text-light-gray mb-5 call-to-action-description">
            Our team is ready to collaborate with you to understand your unique challenges and deliver tailored solutions that drive efficiency, engagement, and profitability. Contact us today to begin your transformation.
          </p>
          <Link
            to="/contact"
            className="btn btn-lg rounded-pill final-cta-button"
            data-aos="zoom-in" data-aos-delay="200"
          >
            Start Your Project Today!
          </Link>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        /* Import Google Font */
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');

        body {
          font-family: 'Poppins', sans-serif;
        }

        .text-gold { color: #FFD700 !important; }
        .text-light-gray { color: #E0E0E0 !important; }
        .text-info { color: #00BCD4 !important; }
        .text-success { color: #4CAF50 !important; }
        .text-warning { color: #FFC107 !important; }
        .text-danger { color: #F44336 !important; }
        .text-light-blue { color: #00BFFF !important; }

        .bg-dark-card { background-color: #2b3e50 !important; }
        .bg-darker-card { background-color: #263238 !important; }
        .bg-gradient-dark {
            background: linear-gradient(145deg, #1A232F, #2C3E50);
        }

        /* Hero Section - New Styles */
        .hero-section-new {
          background-color: #1A232F;
          position: relative;
          overflow: hidden;
          padding-top: 100px;
          padding-bottom: 100px;
          border-bottom: 6px solid #FFD700;
        }

        .background-shapes {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: hidden;
            opacity: 0.15;
        }

        .shape {
            position: absolute;
            background: linear-gradient(45deg, #FFD700, #00BCD4);
            border-radius: 50%;
            filter: blur(80px);
            animation: moveShapes 20s infinite ease-in-out alternate;
        }

        .shape-1 {
            width: 250px;
            height: 250px;
            top: 10%;
            left: -5%;
            animation-delay: 0s;
        }

        .shape-2 {
            width: 350px;
            height: 350px;
            bottom: 5%;
            right: -10%;
            animation-delay: 5s;
            background: linear-gradient(45deg, #00BCD4, #FFC107);
        }

        .shape-3 {
            width: 200px;
            height: 200px;
            top: 40%;
            left: 30%;
            animation-delay: 10s;
            background: linear-gradient(45deg, #4CAF50, #F44336);
        }

        @keyframes moveShapes {
            0% { transform: translate(0, 0) scale(1); }
            25% { transform: translate(20px, -20px) scale(1.05); }
            50% { transform: translate(0, 30px) scale(0.95); }
            75% { transform: translate(-20px, -10px) scale(1.08); }
            100% { transform: translate(0, 0) scale(1); }
        }

        .hero-title-gradient {
            font-size: 3.5rem;
            line-height: 1.1;
            font-weight: 800;
            background: linear-gradient(45deg, #FFD700, #F0E68C); /* Gold to Light Gold */
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: textGradientPulse 3s infinite alternate;
            text-shadow: 0 0 15px rgba(255, 215, 0, 0.4);
        }

        @keyframes textGradientPulse {
            0% { filter: brightness(1); }
            100% { filter: brightness(1.2); }
        }

        .hero-description-new {
          font-size: 1.15rem;
          line-height: 1.8;
          max-width: 700px;
          margin-left: 0; /* Align left */
          margin-right: auto;
          color: #E0E0E0;
          text-shadow: 0 0 5px rgba(0,0,0,0.3);
        }

        .service-list-hero {
          list-style: none;
          padding-left: 0;
          margin-top: 1.5rem;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
          color: #f0f0f0;
          font-size: 1rem;
        }

        .service-list-hero li {
          position: relative;
          padding-left: 28px;
          line-height: 1.6;
        }

        .service-list-hero li::before {
          content: '\\2713'; /* Checkmark unicode */
          color: #00BCD4; /* A vibrant blue for checkmarks */
          position: absolute;
          left: 0;
          font-weight: bold;
          font-size: 1.2rem;
        }

        .hero-cta-button-new {
          background-color: #FFD700 !important;
          color: #1A232F !important;
          padding: 18px 50px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          border: none;
          transition: all 0.3s ease;
          box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
          letter-spacing: 0.5px;
        }

        .hero-cta-button-new:hover {
          background-color: #e6c200 !important;
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 12px 25px rgba(255, 215, 0, 0.5);
        }

        .hero-icon-container {
            width: 250px;
            height: 250px;
            background: rgba(255, 215, 0, 0.1);
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 3px solid rgba(255, 215, 0, 0.5);
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
            transition: all 0.5s ease;
            position: relative;
            z-index: 10;
        }
        .hero-icon-container:hover {
            transform: scale(1.05);
            box-shadow: 0 0 45px rgba(255, 215, 0, 0.8);
            background: rgba(255, 215, 0, 0.2);
        }

        .hero-icon-animated {
            filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.8));
            animation: floatAndGlow 3.5s ease-in-out infinite;
        }

        @keyframes floatAndGlow {
            0% { transform: translateY(0px); filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.8)); }
            50% { transform: translateY(-20px); filter: drop-shadow(0 0 25px rgba(255, 215, 0, 1)); }
            100% { transform: translateY(0px); filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.8)); }
        }

        /* Divider */
        .divider {
          border: 0;
          height: 1px;
          background-image: linear-gradient(to right, rgba(255, 215, 0, 0), rgba(255, 215, 0, 0.75), rgba(255, 215, 0, 0));
          margin: 60px 0;
        }

        /* Section Titles */
        .section-title {
            font-size: 2.8rem;
            margin-bottom: 3.5rem !important;
            position: relative;
            padding-bottom: 15px;
        }
        .section-title::after {
            content: '';
            position: absolute;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
            width: 80px;
            height: 4px;
            background-color: #FFD700;
            border-radius: 2px;
        }

        /* Service Cards */
        .service-card-hover {
          transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out;
          border: 1px solid transparent;
          position: relative;
          overflow: hidden;
          background-color: #263238 !important;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }
        .service-card-hover:hover {
          transform: translateY(-12px);
          box-shadow: 0 18px 35px rgba(0, 0, 0, 0.5);
          border-color: #FFD700;
          background-color: #2b3e50 !important;
        }

        .service-card-title {
          font-size: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .service-card-description {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #CCCCCC;
        }

        .learn-more-button {
          border-color: #FFD700 !important;
          color: #FFD700 !important;
          font-weight: bold;
          padding: 10px 25px;
          transition: all 0.3s ease;
        }
        .learn-more-button:hover {
          background-color: #FFD700 !important;
          color: #1A232F !important;
          box-shadow: 0 5px 15px rgba(255, 215, 0, 0.4);
        }

        /* Detailed Service Sections */
        .detailed-service-section {
          padding-top: 80px;
          padding-bottom: 80px;
        }

        .service-icon-display {
            width: 180px;
            height: 180px;
            border: 2px solid rgba(255, 215, 0, 0.3);
            filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.2));
            transition: all 0.4s ease;
            cursor: pointer;
        }
        .service-icon-display:hover {
            border-color: #FFD700;
            filter: drop-shadow(0 0 25px rgba(255, 215, 0, 0.8));
            transform: scale(1.05);
        }

        .service-icon-display-large {
          width: 280px;
          height: 280px;
          border: 3px solid rgba(255, 215, 0, 0.4);
          filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.4));
          transition: all 0.4s ease;
          cursor: pointer;
        }
        .service-icon-display-large:hover {
          border-color: #FFD700;
          filter: drop-shadow(0 0 35px rgba(255, 215, 0, 1));
          transform: scale(1.03);
        }

        .detailed-service-title {
          font-size: 2rem;
          margin-top: 1.5rem;
        }

        .detailed-service-description {
          font-size: 1rem;
          line-height: 1.8;
          color: #CCCCCC;
        }

        .detailed-feature-list li {
            font-size: 1.05rem;
            color: #F0F0F0;
            margin-bottom: 0.75rem;
        }
        .detailed-feature-list li .text-gold {
            filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5));
        }

        .discover-more-button {
            font-weight: bold;
            padding: 12px 30px;
            transition: all 0.3s ease;
            border-width: 2px !important;
        }
        .discover-more-button:hover {
            background-color: #FFD700 !important;
            color: #1A232F !important;
            border-color: #FFD700 !important;
            box-shadow: 0 5px 15px rgba(255, 215, 0, 0.4);
        }

        /* Why Choose Us Section */
        .why-choose-description {
            font-size: 1.1rem;
            line-height: 1.7;
            max-width: 850px;
        }

        .why-choose-title {
            font-size: 1.6rem;
            margin-top: 1rem;
        }

        .why-choose-text {
            font-size: 0.95rem;
            line-height: 1.6;
            color: #CCCCCC;
        }

        .icon-glow {
          filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.4));
        }

        /* Call to Action Section */
        .call-to-action-section {
          padding-top: 80px;
          padding-bottom: 80px;
          background-image: linear-gradient(rgba(26, 35, 47, 0.9), rgba(26, 35, 47, 0.9)), url('https://via.placeholder.com/1500x500?text=Background+Image');
          background-size: cover;
          background-position: center;
          position: relative;
          z-index: 1;
        }

        .call-to-action-title {
            font-size: 3rem;
            line-height: 1.2;
            text-shadow: 2px 2px 8px rgba(0,0,0,0.5);
        }

        .call-to-action-description {
            font-size: 1.2rem;
            max-width: 900px;
            margin-left: auto;
            margin-right: auto;
            color: #F0F0F0;
        }

        .final-cta-button {
          background-color: #FFD700 !important;
          color: #1A232F !important;
          padding: 18px 55px !important;
          font-weight: 700 !important;
          font-size: 1.35rem !important;
          transition: all 0.3s ease;
          border: none;
        }

        .final-cta-button:hover {
          background-color: #e6c200 !important;
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(255, 215, 0, 0.4);
        }

        /* Responsive Adjustments */
        @media (max-width: 992px) {
            .hero-title-gradient {
                font-size: 2.8rem;
            }
            .hero-icon-container {
                width: 200px;
                height: 200px;
            }
            .hero-icon-animated {
                font-size: 150px !important;
            }
        }

        @media (max-width: 768px) {
            .hero-section-new {
                padding-top: 80px;
                padding-bottom: 80px;
            }
            .hero-title-gradient {
                font-size: 2.2rem;
                text-align: center;
            }
            .hero-description-new {
                font-size: 1rem;
                text-align: center;
                margin-left: auto;
                margin-right: auto;
            }
            .service-list-hero {
                grid-template-columns: 1fr;
                text-align: left; /* Keep checkmarks aligned left */
            }
            .hero-cta-button-new {
                padding: 14px 35px !important;
                font-size: 1.05rem !important;
            }
            .hero-icon-container {
                width: 180px;
                height: 180px;
            }
            .hero-icon-animated {
                font-size: 120px !important;
            }
            .section-title {
                font-size: 2rem;
            }
            .detailed-service-section {
                padding-top: 60px;
                padding-bottom: 60px;
            }
            .detailed-service-title {
                font-size: 1.75rem;
            }
            .service-icon-display, .service-icon-display-large {
                width: 150px;
                height: 150px;
            }
            .service-icon-display-large {
                width: 220px;
                height: 220px;
            }
            .call-to-action-title {
                font-size: 2rem;
            }
            .call-to-action-description {
                font-size: 1rem;
            }
            .final-cta-button {
                padding: 14px 40px !important;
                font-size: 1.1rem !important;
            }
        }
      `}</style>
    </div>
  );
};

export default ServicesPage;